import { Link } from '@tanstack/react-router'
import { useActiveSeasons, useMyProfile } from '../api/hooks'
import type {
  DashboardActiveSeason,
  ProfileGameRatings,
  ProfileRatingScope,
  ProfileRecentPlay,
} from '../api/types'
import { ScreenShell } from '../components/ScreenShell'
import { formatRelativeShort } from '../datetime'
import {
  GameThumb,
  NumberCircle,
  Pill,
  RatingChip,
  ScorePad,
  ScorePadRow,
  SectionHeader,
  TopBar,
  Wordmark,
} from '../primitives'
import { winnerCalcLabel } from '../seasons/format'

/**
 * Home is a personal highlights page: the user's best recent finishes,
 * the games they're rated highest in, and any active seasons. Browsing
 * — groups, friends, all plays — lives in the bottom-nav tabs.
 */
export const HomeScreen = () => {
  const profile = useMyProfile()
  const seasons = useActiveSeasons()

  const bestGames = topFinishes(profile.data?.recentPlays)
  const bestRatings = topRatings(profile.data?.ratings)
  // Only an empty state once both queries have settled — otherwise it
  // flashes in during the profile load.
  const nothingYet =
    !!profile.data &&
    !seasons.isLoading &&
    bestGames.length === 0 &&
    bestRatings.length === 0 &&
    (seasons.data?.length ?? 0) === 0

  return (
    <ScreenShell active="home">
      <TopBar
        title={
          <Link to="/home" aria-label="TopMeeps home" style={{ display: 'inline-flex' }}>
            <Wordmark size={24} />
          </Link>
        }
      />

      {profile.data && (
        <div
          style={{
            padding: '18px 16px 10px',
            fontFamily: 'var(--font-serif)',
            fontSize: 28,
            fontStyle: 'italic',
            color: 'var(--ink)',
            lineHeight: 1.15,
          }}
        >
          Welcome to the table, {profile.data.user.displayName}.
        </div>
      )}

      {nothingYet && <EmptyHome />}

      <BestRecentGamesSection games={bestGames} />
      <BestRatingsSection ratings={bestRatings} />
      <ActiveSeasonsSection seasons={seasons.data} loading={seasons.isLoading} />
    </ScreenShell>
  )
}

const SHOWN = 3

const arrowStyle = {
  fontSize: 22,
  fontWeight: 600,
  color: 'var(--terra)',
  lineHeight: 1,
} as const

// ─── Best recent games ────────────────────────────────────────────────
// "Best" = top finishes. Sort the recent-plays window by rank (1 first),
// tie-broken by recency, and take the top few — so a winner sees wins
// and a non-winner still sees their best recent results.

const topFinishes = (plays: ProfileRecentPlay[] | undefined): ProfileRecentPlay[] =>
  [...(plays ?? [])]
    .sort((a, b) => a.rank - b.rank || b.playedAt.localeCompare(a.playedAt))
    .slice(0, SHOWN)

const BestRecentGamesSection = ({ games }: { games: ProfileRecentPlay[] }) => {
  if (games.length === 0) return null
  return (
    <>
      <SectionHeader title="Best recent games" />
      <div style={{ padding: '0 16px' }}>
        <ScorePad>
          {games.map((p) => (
            <Link
              key={p.playId}
              to="/play/$id"
              params={{ id: p.playId }}
              style={{ display: 'block', color: 'inherit', textDecoration: 'none' }}
            >
              <ScorePadRow>
                <NumberCircle n={p.rank} winner={p.rank === 1} size={22} />
                <GameThumb label={p.game.name} imageUrl={p.game.imageUrl} size={32} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--ink)' }}>
                    {p.game.name}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>
                    {p.groupName ?? '(no group)'} · {formatRelativeShort(p.playedAt)}
                  </div>
                </div>
                <span style={arrowStyle}>→</span>
              </ScorePadRow>
            </Link>
          ))}
        </ScorePad>
      </div>
    </>
  )
}

// ─── Best game ratings ────────────────────────────────────────────────
// Each game carries several rating scopes (cross-group + per-group).
// Pick a headline scope — the highest non-provisional one, falling back
// to the highest scope overall — then rank games by it.

type RankedRating = { game: ProfileGameRatings['game']; scope: ProfileRatingScope }

const headlineScope = (r: ProfileGameRatings): ProfileRatingScope | null => {
  const scopes = [r.crossGroup, ...r.perGroup].filter((s): s is ProfileRatingScope => s !== null)
  if (scopes.length === 0) return null
  const ranked = scopes.filter((s) => !s.isProvisional)
  const pool = ranked.length > 0 ? ranked : scopes
  return pool.reduce((best, s) => (s.mu > best.mu ? s : best))
}

const topRatings = (ratings: ProfileGameRatings[] | undefined): RankedRating[] =>
  (ratings ?? [])
    .map((r) => ({ game: r.game, scope: headlineScope(r) }))
    .filter((x): x is RankedRating => x.scope !== null)
    .sort((a, b) => b.scope.mu - a.scope.mu)
    .slice(0, SHOWN)

const BestRatingsSection = ({ ratings }: { ratings: RankedRating[] }) => {
  if (ratings.length === 0) return null
  return (
    <>
      <SectionHeader title="Best game ratings" />
      <div style={{ padding: '0 16px' }}>
        <ScorePad>
          {ratings.map(({ game, scope }) => (
            <ScorePadRow key={game.id}>
              <GameThumb label={game.name} imageUrl={game.imageUrl} size={32} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--ink)' }}>
                  {game.name}
                </div>
                <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>
                  {scope.groupName ?? 'Cross-group'}
                </div>
              </div>
              <RatingChip mu={scope.mu} tier={scope.tier} compact />
            </ScorePadRow>
          ))}
        </ScorePad>
      </div>
    </>
  )
}

// ─── Active seasons ───────────────────────────────────────────────────

const ActiveSeasonsSection = ({
  seasons,
  loading,
}: {
  seasons: DashboardActiveSeason[] | undefined
  loading: boolean
}) => {
  if (loading) return null
  if (!seasons || seasons.length === 0) return null
  return (
    <>
      <SectionHeader title="Active seasons" />
      <div style={{ padding: '0 16px' }}>
        <ScorePad>
          {seasons.map((s) => (
            <ScorePadRow
              key={s.seasonId}
              style={{ alignItems: 'flex-start', padding: '12px 14px' }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <Link
                  to="/season/$id"
                  params={{ id: s.seasonId }}
                  style={{
                    display: 'block',
                    fontFamily: 'var(--font-display)',
                    fontWeight: 600,
                    fontSize: 14,
                    color: 'var(--ink)',
                    textDecoration: 'none',
                  }}
                >
                  {s.name}
                </Link>
                <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 2 }}>
                  {s.groupName} · {winnerCalcLabel(s.winnerCalcMethod)}
                </div>
              </div>
              <Pill color={s.daysRemaining <= 7 ? 'terra' : 'gilt'}>
                {s.daysRemaining === 0 ? 'last day' : `${s.daysRemaining}d left`}
              </Pill>
            </ScorePadRow>
          ))}
        </ScorePad>
      </div>
    </>
  )
}

const EmptyHome = () => (
  <div style={{ padding: '4px 16px' }}>
    <div className="felt-card" style={{ padding: 16 }}>
      <div
        style={{
          fontFamily: 'var(--font-serif)',
          fontStyle: 'italic',
          fontSize: 20,
          color: 'var(--ink)',
        }}
      >
        Nothing on the table yet.
      </div>
      <div style={{ marginTop: 6, fontSize: 13, color: 'var(--ink-2)' }}>
        Tap the <strong style={{ color: 'var(--terra)' }}>+</strong> below to record your first play
        — pick a group, or just friends.
      </div>
    </div>
  </div>
)
